import LoadingComponent from '@components/animation/loading-component';
import {
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableContainerProps,
  TableHead,
  TablePagination,
  TableProps,
  TableRow,
  Typography,
} from '@mui/material';
import { handleTitleCamelCase } from '@utils/keypress';
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { NoRowsOverlay } from './datatable';
import { GridRowId } from '@mui/x-data-grid';

export interface TableRowItem {
  value: string;
  cellProps?: TableCellProps;
  headerCellProps?: TableCellProps;
}

interface CustomTableRowProps {
  row: Record<string, TableRowItem>;
  collapseKey?: {
    title?: string;
    rows: Record<string, TableRowItem>[];
  };
  headCellProps?:
    | TableCellProps
    | ((key: string, item?: TableRowItem) => TableCellProps);
  bodyCellProps?:
    | TableCellProps
    | ((key: string, item?: TableRowItem) => TableCellProps);
  rowIndex: number;
  rowId: number | string;
  selectedRows?: (number | string)[];
  hideColumns?: string[];
  handleToggleCheckbox: (index: number | string) => void;
  singleSelect?: boolean;
}

interface CustomTableProps {
  /** Array of main rows (each is a Record of column -> TableRowItem) */
  rows: Record<string, TableRowItem>[];

  /** Nested rows for collapsible section */
  collapseRows?: Record<string, TableRowItem>[][];

  /** Title for collapse section */
  collapseTitle?: string;

  /** Optional custom column headers */
  columns?: Record<string, string>;

  /** Optional props */
  tableContainerProps?: TableContainerProps;
  tableProps?: TableProps;
  headCellProps?:
    | TableCellProps
    | ((key: string, item?: TableRowItem) => TableCellProps);
  bodyCellProps?:
    | TableCellProps
    | ((key: string, item?: TableRowItem) => TableCellProps);
  loading?: boolean;

  checkboxSelection?: boolean;
  sx?: SxProps;
  onSelectionChange?: (selectedRows: (string | number)[]) => void;
  getRowId: (row: Record<string, TableRowItem>) => string | number;
  hideColumns?: string[];
  singleSelect?: boolean;

  /** Pagination props */
  pagination?: boolean;
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: number[];
}

export interface ICustomTableFunc {
  getSelectedRows: () => GridRowId[];
  handleSearch: (
    query: string,
    setSearchValue?: (params: string) => void,
  ) => void;
}

const resolveProps = (
  prop:
    | CustomTableRowProps['bodyCellProps']
    | CustomTableRowProps['headCellProps'],
  key: string,
  item?: TableRowItem,
) => {
  if (typeof prop === 'function') return prop(key, item);
  return prop || {};
};

const CustomTableRow = ({
  row,
  collapseKey,
  headCellProps,
  bodyCellProps,
  rowIndex,
  selectedRows,
  handleToggleCheckbox,
  rowId,
  hideColumns,
  singleSelect,
}: CustomTableRowProps) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          bgcolor:
            singleSelect && selectedRows?.includes(rowId)
              ? 'primary.300'
              : rowIndex % 2 === 0
                ? '#f5f5f5'
                : 'default',
        }}
        onClick={singleSelect ? () => handleToggleCheckbox(rowId) : undefined}
      >
        <TableCell sx={{ py: '3px', pr: 0, whiteSpace: 'nowrap' }}>
          {selectedRows && !singleSelect && (
            <Checkbox
              size='small'
              checked={selectedRows.includes(rowId)}
              onChange={() => handleToggleCheckbox(rowId)}
            />
          )}
          {collapseKey && (
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
              disableRipple
              sx={{ bgcolor: 'transparent' }}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>

        {Object.entries(row)
          .filter(([key]) => !hideColumns?.includes(key))
          .map(([key, item]) => (
            <TableCell
              key={key}
              {...resolveProps(bodyCellProps, key, item)}
              {...item.cellProps}
              sx={{
                whiteSpace: 'nowrap',
                ...((typeof resolveProps(bodyCellProps, key, item)?.sx ===
                'object'
                  ? resolveProps(bodyCellProps, key, item)?.sx
                  : {}) as Record<string, unknown>),
                ...((item.cellProps?.sx || {}) as Record<string, unknown>),
                py: '12px',
              }}
            >
              {item.value}
            </TableCell>
          ))}
      </TableRow>

      {collapseKey && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout='auto' unmountOnExit>
              <Box sx={{ margin: 1 }}>
                {collapseKey.title && (
                  <Typography variant='h6' gutterBottom component='div'>
                    {collapseKey.title}
                  </Typography>
                )}
                <Table size='small' sx={{ width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      {Object.keys(collapseKey?.rows?.[0] || {}).map((key) => {
                        const item = collapseKey?.rows?.[0]?.[key];
                        return (
                          <TableCell
                            key={key}
                            {...resolveProps(headCellProps, key, item)}
                            {...item.headerCellProps}
                            sx={{
                              whiteSpace: 'nowrap',
                              ...((typeof resolveProps(
                                item.headerCellProps,
                                key,
                                item,
                              )?.sx === 'object'
                                ? resolveProps(item.headerCellProps, key, item)
                                    ?.sx
                                : {}) as Record<string, unknown>),
                            }}
                          >
                            {handleTitleCamelCase(key)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {collapseKey?.rows?.map((subRow, index) => (
                      <TableRow key={index}>
                        {Object.entries(subRow)
                          .filter(([key]) => !hideColumns?.includes(key))
                          .map(([key, item]) => (
                            <TableCell
                              key={key}
                              {...resolveProps(bodyCellProps, key, item)}
                              {...item.cellProps}
                              sx={
                                index === collapseKey.rows.length - 1
                                  ? {
                                      whiteSpace: 'nowrap',
                                      borderBottom: 0,
                                      ...item.cellProps?.sx,
                                    }
                                  : {
                                      whiteSpace: 'nowrap',
                                      ...item.cellProps?.sx,
                                    }
                              }
                            >
                              {item.value}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/**
 * CustomTable
 *
 * A reusable, dynamic table component supporting collapsible rows and
 * customizable headers, cells, and nested sub-tables.
 *
 * ## Features
 * - Dynamic columns from `columns` or derived from `rows`.
 * - Collapsible row sections via `collapseRows` and `collapseTitle`.
 * - Supports both static and dynamic `headCellProps` and `bodyCellProps`.
 * - Allows individual per-cell `cellProps` and nested cell configuration.
 *
 * ## Props
 * @param rows - The main table data, each key representing a column.
 * @param columns - Optional object mapping keys to column display names.
 * @param collapseRows - Optional nested rows shown when expanded.
 * @param collapseTitle - Optional title for the collapsible section.
 * @param tableContainerProps - Props passed to the MUI TableContainer.
 * @param tableProps - Props passed to the MUI Table.
 * @param headCellProps - Static or function-based props for header cells.
 * @param bodyCellProps - Static or function-based props for body cells.
 *
 * ## Example Usage
 * ```tsx
 * <CustomTable
 *   rows={[
 *     {
 *       name: { value: 'Deluxe Room' },
 *       rate: { value: '$150', cellProps: { align: 'right' } },
 *     },
 *     {
 *       name: { value: 'Superior Room' },
 *       rate: { value: '$180', cellProps: { align: 'right' } },
 *     },
 *   ]}
 *   collapseTitle="Room Details"
 *   collapseRows={[
 *     {
 *       feature: {
 *         value: 'Size',
 *         headerCellProps: {
 *           align: 'left',
 *           sx: { fontWeight: 'bold' },
 *         },
 *         cellProps: { align: 'left' },
 *       },
 *       detail: {
 *         value: '25 sqm',
 *         headerCellProps: { align: 'right' },
 *         cellProps: { align: 'right' },
 *       },
 *     },
 *     {
 *       feature: { value: 'Beds', cellProps: { align: 'left' } },
 *       detail: { value: '1 Queen', cellProps: { align: 'right' } },
 *     },
 *   ]}
 *   columns={{
 *     name: 'Room Name',
 *     rate: 'Rate (USD)',
 *   }}
 *   tableProps={{ size: 'small' }}
 *   headCellProps={(key) =>
 *     key === 'name'
 *       ? { align: 'left', sx: { fontWeight: 'bold' } }
 *       : { align: 'right' }
 *   }
 *   bodyCellProps={(key) =>
 *     key === 'rate'
 *       ? {
 *           align: 'right',
 *           sx: { color: 'primary.main', fontWeight: 500 },
 *         }
 *       : { align: 'left' }
 *   }
 * />
 * ```
 */
const CustomTable = forwardRef<ICustomTableFunc, CustomTableProps>(
  (
    {
      rows,
      collapseRows,
      collapseTitle,
      columns,
      tableContainerProps,
      tableProps,
      headCellProps,
      bodyCellProps,
      loading,
      pagination = false,
      defaultRowsPerPage = 10,
      rowsPerPageOptions = [5, 10, 25, 50],
      checkboxSelection = false,
      sx,
      onSelectionChange,
      getRowId,
      hideColumns,
      singleSelect,
    },
    ref,
  ) => {
    const columnKeys = Object.keys(columns || rows?.[0] || {}).filter(
      (key) => !hideColumns?.includes(key),
    );

    const resolveHeadProps = (key: string): TableCellProps => {
      if (typeof headCellProps === 'function') return headCellProps(key);
      return headCellProps || {};
    };

    // #region SEARCH
    const [filteredRows, setFilteredRows] =
      useState<Record<string, TableRowItem>[]>(rows);

    const handleSearch = (
      query: string,
      setSearchValue?: (params: string) => void,
    ) => {
      setSearchValue?.(query);

      if (!query.trim()) {
        setFilteredRows(rows);
        return;
      }

      const lowerQuery = query.toLowerCase();

      const newFiltered = rows.filter((row) =>
        Object.values(row).some((item) =>
          String(item.value).toLowerCase().includes(lowerQuery),
        ),
      );

      setFilteredRows(newFiltered);
    };

    // #endregion

    // #region PAGINATION
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

    const handleChangePage = (_: unknown, newPage: number) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const newValue = parseInt(event.target.value, 10);
      setRowsPerPage(newValue);
      setPage(0);
    };
    // #endregion

    // #region SELECTION
    const resolveRowId = (row: Record<string, TableRowItem>, index: number) => {
      if (getRowId) return getRowId(row);
      // fallback: use a value from the row or index
      return row.id?.value ?? index;
    };

    const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);

    const handleToggleCheckbox = (rowId: string | number) => {
      setSelectedRows((prev) => {
        const updated = prev.includes(rowId)
          ? prev.filter((i) => i !== rowId)
          : singleSelect
            ? [rowId]
            : [...prev, rowId];

        onSelectionChange?.(updated);
        return updated;
      });
    };

    const getSelectedRows = () => selectedRows;
    // #endregion

    // #region SORTING
    const [sortConfig, setSortConfig] = useState<{
      key: string | null;
      direction: 'asc' | 'desc';
    }>({ key: null, direction: 'asc' });

    const handleSort = (key: string) => {
      setSortConfig((prev) => {
        if (prev.key === key) {
          // toggle between asc/desc
          return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        }
        return { key, direction: 'asc' };
      });
    };

    const sortedRows = useMemo(() => {
      if (!sortConfig.key) return filteredRows;

      return [...filteredRows].sort((a, b) => {
        const aItem = a[sortConfig.key!];
        const bItem = b[sortConfig.key!];
        const aVal = aItem?.value;
        const bVal = bItem?.value;

        // handle null/undefined
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        // Detect date-like strings
        const isDateLike = (val: unknown): boolean => {
          if (val instanceof Date) return true;
          if (typeof val === 'string') {
            return /^\d{4}-\d{2}-\d{2}/.test(val); // simple ISO-like check
          }
          return false;
        };

        // handle date strings or Date objects
        if (isDateLike(aVal) && isDateLike(bVal)) {
          const aDate = new Date(aVal as any);
          const bDate = new Date(bVal as any);
          return sortConfig.direction === 'asc'
            ? aDate.getTime() - bDate.getTime()
            : bDate.getTime() - aDate.getTime();
        }

        // handle strings
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        // handle numbers
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        return 0;
      });
    }, [filteredRows, sortConfig]);

    const { paginatedRows, isHaveRow } = useMemo(() => {
      if (rowsPerPage === -1)
        return { paginatedRows: sortedRows, isHaveRow: sortedRows?.length > 0 };
      const start = page * rowsPerPage;

      return {
        paginatedRows: sortedRows.slice(start, start + rowsPerPage),
        isHaveRow: sortedRows?.length > 0,
      };
    }, [sortedRows, page, rowsPerPage]);
    // #endregion

    useLayoutEffect(() => {
      setFilteredRows(rows);
      setSelectedRows([]);
      setPage(0);
    }, [rows]);

    useImperativeHandle(ref, () => ({ getSelectedRows, handleSearch }));

    return (
      <Stack sx={{ ...sx, position: 'relative', flex: 1, minHeight: 0 }}>
        {loading && <LoadingComponent />}

        <TableContainer
          {...tableContainerProps}
          sx={{
            ...tableContainerProps?.sx,
            flex: 1,
            minHeight: 0,
            position: 'relative',
          }}
        >
          <Table {...tableProps} size={tableProps?.size || 'small'}>
            <TableHead sx={{ height: '56px' }}>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                    whiteSpace: 'nowrap',
                    pr: 0,
                  }}
                >
                  {checkboxSelection && !singleSelect && (
                    <Checkbox
                      size='small'
                      indeterminate={
                        selectedRows.length > 0 &&
                        selectedRows.length < rows.length
                      }
                      checked={
                        rows.length > 0 && selectedRows.length === rows.length
                      }
                      onChange={() =>
                        setSelectedRows(
                          selectedRows.length === rows.length
                            ? []
                            : rows.map((r, i) => resolveRowId(r, i)),
                        )
                      }
                      disabled={!isHaveRow}
                    />
                  )}
                </TableCell>
                {columnKeys.map((key) => (
                  <TableCell
                    key={key}
                    {...resolveHeadProps(key)}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'background.paper',
                      zIndex: 1,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      ...resolveHeadProps(key)?.sx,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '35%',
                        bottom: '35%',
                        width: '1px',
                        backgroundColor: 'rgba(224, 224, 224, 1)',
                      },
                    }}
                    onClick={() => handleSort(key)}
                  >
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                      <Typography variant='body2' fontWeight={600}>
                        {columns?.[key] ||
                          handleTitleCamelCase(
                            key.charAt(0).toUpperCase() + key.slice(1),
                            ['ID'],
                          )}
                      </Typography>
                      {sortConfig.key === key &&
                        (sortConfig.direction === 'asc' ? (
                          <ArrowUpwardOutlined
                            fontSize='small'
                            sx={{ fontSize: 14 }}
                          />
                        ) : (
                          <ArrowDownwardOutlined
                            fontSize='small'
                            sx={{ fontSize: 14 }}
                          />
                        ))}
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isHaveRow &&
                paginatedRows.map((row, index) => (
                  <CustomTableRow
                    key={index}
                    rowIndex={index}
                    row={row}
                    collapseKey={
                      collapseRows
                        ? { title: collapseTitle, rows: collapseRows[index] }
                        : undefined
                    }
                    rowId={resolveRowId(row, index)}
                    headCellProps={headCellProps}
                    bodyCellProps={bodyCellProps}
                    selectedRows={checkboxSelection ? selectedRows : undefined}
                    handleToggleCheckbox={handleToggleCheckbox}
                    hideColumns={hideColumns}
                    singleSelect={singleSelect}
                  />
                ))}
            </TableBody>
          </Table>
          {!isHaveRow && (
            <Stack
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <NoRowsOverlay />
            </Stack>
          )}
        </TableContainer>

        {pagination && (
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {selectedRows.length > 0 && (
              <Typography
                sx={{
                  fontSize: 12,
                  pl: 1,
                }}
              >
                {`${selectedRows.length} ${selectedRows.length > 1 ? 'rows' : 'row'} selected`}
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />

            <TablePagination
              component='div'
              size='small'
              sx={{ minHeight: '52px' }}
              count={filteredRows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[
                ...rowsPerPageOptions,
                { label: 'All', value: -1 },
              ]}
              labelDisplayedRows={({ from, to, count }) =>
                rowsPerPage === -1
                  ? `Showing all ${count !== -1 ? count : `more than ${to}`}`
                  : `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </Stack>
        )}
      </Stack>
    );
  },
);

export default CustomTable;
